"use client";
import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { ArrowLeftIcon, ClockIcon, Loader2, XIcon, MapPinIcon } from "lucide-react";
import {
  useI18N,
  useAuth,
  useRecentSearches,
  useAllRecentSearches,
  useNeighborhoodSuggestions,
  useAllNeighborhoodSuggestions,
  useAccessibilityTags,
  useAccessibility,
  useZoomDetection
} from "common/hooks";
import { usePostDeleteSavedSearch } from "common/hooks/saved-search";
import { RecentSearchResult, NeighbourhoodSuggestionResult } from "common/apis";
import { cn } from "@/lib/utils";
import { CrosshairIcon, MapTrifoldIcon } from "@phosphor-icons/react";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useCustomToast } from "@/hooks/useCustomToast";
import AnalyticsService from "@/services/analytics";
import { useQueryClient } from "@tanstack/react-query";
import {
  format,
  isToday,
  isYesterday,
  parseISO,
  startOfWeek,
  subWeeks,
  differenceInDays,
} from "date-fns";

// ============================================================================
// Types
// ============================================================================

// Re-export types for external use
export type { RecentSearchResult, NeighbourhoodSuggestionResult };

// Legacy types for backward compatibility
export interface RecentSearch {
  id: string;
  text: string;
}

export interface Neighborhood {
  id: string;
  name: string;
}

// Type for neighborhood from the API
export type NeighborhoodSuggestion =
  NeighbourhoodSuggestionResult["searches"][0];

// View state for inline navigation
type ViewState = "main" | "allRecentSearches" | "allNeighborhoods";

// Search section for date grouping
interface SearchSection {
  title: string;
  data: RecentSearchResult[];
  subtitle?: string;
}

// Accessibility option for filter display
interface AccessibilityOption {
  id: number;
  name: string;
}

export interface SearchSuggestionsDropdownProps {
  isOpen: boolean;
  onNearbyPlacesClick: () => void;
  onRecentSearchSelect: (search: RecentSearchResult) => void;
  onNeighborhoodSelect: (neighborhood: NeighborhoodSuggestion) => void;
  onLoginPress?: () => void;
  onClose?: () => void;
  className?: string;
  onKeyboardNavigate?: (direction: "up" | "down") => void;
  latitude?: number | null;
  longitude?: number | null;
}

// ============================================================================
// Component
// ============================================================================

export function SearchSuggestionsDropdown({
  isOpen,
  onNearbyPlacesClick,
  onRecentSearchSelect,
  onNeighborhoodSelect,
  onLoginPress,
  onClose,
  className,
  onKeyboardNavigate,
  latitude,
  longitude,
}: SearchSuggestionsDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const i18n = useI18N();
  const { isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const showToast = useCustomToast();
  const queryClient = useQueryClient();
  const isZoomedIn = useZoomDetection(1.25);

  // View state for inline navigation
  const [currentView, setCurrentView] = useState<ViewState>("main");
  
  // State for keyboard navigation
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  
  // State for keyboard navigation in expanded views
  const [expandedHighlight, setExpandedHighlight] = useState<number>(-1);
  const expandedItemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Track which search is being deleted for transition effect
  const [deletingSearchId, setDeletingSearchId] = useState<number | null>(null);

  // Fetch data using hooks
  const { data: recentSearchesData, isLoading: isRecentSearchesLoading } =
    useRecentSearches();

  // Fetch all recent searches when expanded view is active
  const { data: allRecentSearchesData, isLoading: isAllRecentSearchesLoading } =
    useAllRecentSearches(currentView === "allRecentSearches");

  const {
    data: neighbourhoodSuggestions,
    isLoading: isNeighbourhoodSuggestionsLoading,
  } = useNeighborhoodSuggestions(latitude, longitude);

  // Fetch all neighborhood suggestions when expanded view is active
  const {
    data: allNeighbourhoodSuggestionsData,
    isLoading: isAllNeighbourhoodSuggestionsLoading,
  } = useAllNeighborhoodSuggestions(currentView === "allNeighborhoods", latitude, longitude);

  const {
    deleteSavedSearch,
    isLoading: isDeleting,
    status: deleteStatus,
    error: deleteError,
  } = usePostDeleteSavedSearch();

  // Fetch accessibility data for filter display
  const { accessibilityTags } = useAccessibilityTags();
  const { filterAccessibility } = useAccessibility();

  // Process recent searches from paginated data (for main view)
  const recentSearches = useMemo(() => {
    if (!recentSearchesData?.pages) return [];
    return recentSearchesData.pages.flatMap((page) => page.searches);
  }, [recentSearchesData]);

  // All recent searches for expanded view
  const allRecentSearches = useMemo(() => {
    if (!allRecentSearchesData?.searches) return [];
    return allRecentSearchesData.searches;
  }, [allRecentSearchesData]);

  // Get neighborhood suggestions (for main view)
  const neighborhoods = useMemo(() => {
    if (!neighbourhoodSuggestions?.searches) return [];
    return neighbourhoodSuggestions.searches;
  }, [neighbourhoodSuggestions]);

  // All neighborhood suggestions for expanded view
  const allNeighborhoods = useMemo(() => {
    if (!allNeighbourhoodSuggestionsData?.searches) return [];
    return allNeighbourhoodSuggestionsData.searches;
  }, [allNeighbourhoodSuggestionsData]);

  // Limit displayed items to 5
  const displayedRecentSearches = recentSearches.slice(0, 5);
  const displayedNeighborhoods = neighborhoods.slice(0, 5);

  // CheckIcon if there are more items
  const hasMoreRecentSearches = recentSearches.length >= 5;
  const hasMoreNeighborhoods = neighborhoods.length >= 5;
  
  // Calculate total navigable items for main view
  const totalItems = 1 + // Nearby Places
    (isLoggedIn ? displayedRecentSearches.length : 0) + 
    (isLoggedIn && hasMoreRecentSearches ? 1 : 0) + // More link for recent searches
    displayedNeighborhoods.length +
    (hasMoreNeighborhoods ? 1 : 0); // More link for neighborhoods

  // Get localized date section titles
  const getLocalizedSectionTitle = useCallback(
    (key: string): string => {
      switch (key) {
        case "Today":
          return i18n.get("today");
        case "Yesterday":
          return i18n.get("yesterday");
        case "This Week":
          return i18n.get("thisWeek");
        case "Last Week":
          return i18n.get("lastWeek");
        case "This Month":
          return i18n.get("thisMonth");
        default:
          return key; // For month names like"December 2024", keep as-is
      }
    },
    [i18n],
  );

  // Group all recent searches by date sections (for expanded view)
  const recentSearchSections = useMemo(() => {
    if (allRecentSearches.length === 0) return [];

    const sectionsMap = new Map<
      string,
      { searches: RecentSearchResult[]; order: number; subtitle?: string }
    >();
    const now = new Date();
    const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
    const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });

    allRecentSearches.forEach((search) => {
      const searchDate = parseISO(search.created_at);
      const daysDiff = differenceInDays(now, searchDate);

      let sectionKey: string;
      let order: number;
      let subtitle: string | undefined;

      if (isToday(searchDate)) {
        sectionKey = "Today";
        order = 0;
        subtitle = format(now, "EEEE, MMMM d");
      } else if (isYesterday(searchDate)) {
        sectionKey = "Yesterday";
        order = 1;
        subtitle = format(searchDate, "EEEE, MMMM d");
      } else if (daysDiff < 7 && searchDate >= thisWeekStart) {
        sectionKey = "This Week";
        order = 2;
      } else if (daysDiff < 14 && searchDate >= lastWeekStart) {
        sectionKey = "Last Week";
        order = 3;
      } else if (daysDiff < 30) {
        sectionKey = "This Month";
        order = 4;
      } else {
        // Group by month for older searches
        sectionKey = format(searchDate, "MMMM yyyy");
        order = 5 + Math.floor(daysDiff / 30);
      }

      if (!sectionsMap.has(sectionKey)) {
        sectionsMap.set(sectionKey, { searches: [], order, subtitle });
      }

      sectionsMap.get(sectionKey)!.searches.push(search);
    });

    // Convert map to array and sort by order
    const sectionsArray: SearchSection[] = Array.from(sectionsMap.entries())
      .sort((a, b) => a[1].order - b[1].order)
      .map(([title, { searches, subtitle }]) => ({
        title: getLocalizedSectionTitle(title),
        data: searches,
        subtitle,
      }));

    return sectionsArray;
  }, [allRecentSearches, getLocalizedSectionTitle]);

  // Reset view when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentView("main");
      setHighlightedIndex(-1);
      setExpandedHighlight(-1);
    }
  }, [isOpen]);
  
  // Reset highlight when view changes
  useEffect(() => {
    setHighlightedIndex(-1);
    setExpandedHighlight(-1);
  }, [currentView]);
  
  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlightedIndex]);
  
  // Scroll expanded view highlighted item into view
  useEffect(() => {
    if (expandedHighlight >= 0 && expandedItemRefs.current[expandedHighlight]) {
      expandedItemRefs.current[expandedHighlight]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [expandedHighlight]);
  
  // Handle keyboard navigation from parent (main view)
  useEffect(() => {
    if (!onKeyboardNavigate || currentView !== "main") return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev < totalItems - 1 ? prev + 1 : prev;
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : 0;
          return next;
        });
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
          itemRefs.current[highlightedIndex]?.click();
        }
      } else if (e.key === "Home") {
        e.preventDefault();
        setHighlightedIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setHighlightedIndex(totalItems - 1);
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [highlightedIndex, totalItems, onKeyboardNavigate, currentView]);
  
  // Handle keyboard navigation for expanded views (only for search result buttons, not delete buttons)
  useEffect(() => {
    if (currentView === "main") return;
    
    const totalExpandedItems = currentView === "allRecentSearches" 
      ? recentSearchSections.reduce((acc, section) => acc + section.data.length, 0) + 1 // +1 for back button
      : allNeighborhoods.length + 1; // +1 for back button
    
    const handleExpandedKeyDown = (e: KeyboardEvent) => {
      // Only handle if focused element is not the delete button
      const activeElement = document.activeElement;
      const isDeleteButton = activeElement?.getAttribute('aria-label')?.includes('Delete');
      if (isDeleteButton) return;
      
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const nextIndex = expandedHighlight + 1;
        if (nextIndex < totalExpandedItems) {
          expandedItemRefs.current[nextIndex]?.focus();
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prevIndex = expandedHighlight - 1;
        if (prevIndex >= 0) {
          expandedItemRefs.current[prevIndex]?.focus();
        }
      } else if (e.key === "Home") {
        e.preventDefault();
        expandedItemRefs.current[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        expandedItemRefs.current[totalExpandedItems - 1]?.focus();
      }
    };
    
    window.addEventListener("keydown", handleExpandedKeyDown);
    return () => window.removeEventListener("keydown", handleExpandedKeyDown);
  }, [currentView, expandedHighlight, recentSearchSections, allNeighborhoods]);
  
  // Focus first item when entering expanded views
  useEffect(() => {
    if (currentView !== "main") {
      // Focus the back button when entering expanded view
      setTimeout(() => {
        expandedItemRefs.current[0]?.focus();
      }, 100);
    }
  }, [currentView]);
  
  // Announce view changes to screen readers
  useEffect(() => {
    const announcement = currentView === "allRecentSearches" 
      ? i18n.get("allRecentSearches")
      : currentView === "allNeighborhoods"
      ? i18n.get("allSuggestedBySociability")
      : null;
    
    if (announcement) {
      // Create temporary live region for announcement
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      liveRegion.textContent = announcement;
      document.body.appendChild(liveRegion);
      
      // Remove after announcement
      setTimeout(() => {
        if (document.body.contains(liveRegion)) {
          document.body.removeChild(liveRegion);
        }
      }, 1000);
    }
  }, [currentView, i18n]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setCurrentView("main");
        onClose?.();
      }
    };

    if (isOpen && onClose) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key - go back to main view first, then close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (currentView !== "main") {
          setCurrentView("main");
        } else {
          onClose?.();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, currentView, onClose]);

  // Helper to get accessibility tag names from IDs
  const getAccessibilityOptions = useCallback(
    (accessibilityIds: (string | number)[]): AccessibilityOption[] => {
      if (!accessibilityIds || accessibilityIds.length === 0) {
        return [];
      }

      // Prefer filterAccessibility from dedicated hook, fall back to search-based tags
      const tagsToUse =
        filterAccessibility.length > 0
          ? filterAccessibility
          : accessibilityTags || [];

      if (tagsToUse.length === 0) {
        return [];
      }

      const tagsMap = new Map<string, string>();
      tagsToUse.forEach((option: any) => {
        if (option.accessibility_tags) {
          option.accessibility_tags.forEach((tag: any) => {
            tagsMap.set(tag.id.toString(), tag.name);
          });
        }
      });

      return accessibilityIds
        .map((id) => {
          const stringId = id.toString();
          const name = tagsMap.get(stringId);
          if (name) {
            return { id: parseInt(stringId, 10), name };
          }
          return null;
        })
        .filter(Boolean) as AccessibilityOption[];
    },
    [filterAccessibility, accessibilityTags],
  );

  // Helper to format filters text
  const formatFilters = useCallback(
    (filters: any): string | null => {
      if (!filters) return null;

      const parts: string[] = [];

      // Business types (e.g.,"Cafe, Bar")
      if (filters.business_types?.length > 0) {
        parts.push(filters.business_types.join(", "));
      }

      // Accessibility features (e.g.,"Step-free, Small bump")
      const accessibilityOptions = getAccessibilityOptions(
        filters.accessibilities || [],
      );

      if (accessibilityOptions.length > 0) {
        parts.push(accessibilityOptions.map((o) => o.name).join(", "));
      }

      // Opening hours - show the time range if available
      if (filters.opening_hours) {
        const { day, start, end } = filters.opening_hours;
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        let timeText = "";
        if (day && Array.isArray(day) && day.length > 0) {
          timeText += days[day[0]] + " ";
        }
        if (start) {
          timeText += start;
          if (end) {
            timeText += `-${end}`;
          }
        }
        if (timeText) {
          parts.push(timeText.trim());
        }
      }

      // Open now - show this in addition to opening hours if set
      if (filters.open_now) {
        parts.push("Open now");
      }

      return parts.length > 0 ? parts.join(" â€¢ ") : null;
    },
    [getAccessibilityOptions],
  );

  // Handle back button
  const handleBack = useCallback(() => {
    // Track analytics
    AnalyticsService.trackGAEventHandler("searchSuggestionsBackClicked");
    AnalyticsService.trackMx("searchSuggestionsBackClicked", {
      FromView: currentView,
    });

    setCurrentView("main");
  }, [currentView]);

  const handleViewMoreRecentSearches = useCallback(() => {
    if (!isLoggedIn) {
      // Track analytics
      AnalyticsService.trackGAEventHandler("searchSuggestionsLoginPromptShown");
      AnalyticsService.trackMx("searchSuggestionsLoginPromptShown", {});

      showModal(AuthModalScreen.EnterEmail);
      return;
    }

    // Track analytics
    AnalyticsService.trackGAEventHandler(
      "searchSuggestionsViewMoreRecentSearches",
    );
    AnalyticsService.trackMx("searchSuggestionsViewMoreRecentSearches", {});

    setCurrentView("allRecentSearches");
  }, [isLoggedIn, showModal]);

  const handleLoginClick = useCallback(() => {
    // Track analytics
    AnalyticsService.trackGAEventHandler("searchSuggestionsLoginClicked");
    AnalyticsService.trackMx("searchSuggestionsLoginClicked", {});

    showModal(AuthModalScreen.EnterEmail);
    onLoginPress?.();
  }, [showModal, onLoginPress]);

  const handleViewMoreNeighborhoods = useCallback(() => {
    // Track analytics
    AnalyticsService.trackGAEventHandler(
      "searchSuggestionsViewMoreNeighborhoods",
    );
    AnalyticsService.trackMx("searchSuggestionsViewMoreNeighborhoods", {});

    setCurrentView("allNeighborhoods");
  }, []);

  // Handler for selecting from expanded view (reset view and close)
  const handleRecentSearchSelectFromExpandedView = useCallback(
    (search: RecentSearchResult) => {
      // Track analytics
      AnalyticsService.trackGAEventHandler("allRecentSearchesSearchSelected");
      AnalyticsService.trackMx("allRecentSearchesSearchSelected", {
        SearchId: search?.id,
        SearchTerm: search?.search_term,
      });

      setCurrentView("main");
      onClose?.();
      onRecentSearchSelect(search);
    },
    [onClose, onRecentSearchSelect],
  );

  const handleNeighborhoodSelectFromExpandedView = useCallback(
    (neighborhood: NeighborhoodSuggestion) => {
      // Track analytics
      AnalyticsService.trackGAEventHandler(
        "allNeighborhoodsNeighborhoodSelected",
      );
      AnalyticsService.trackMx("allNeighborhoodsNeighborhoodSelected", {
        NeighborhoodId: neighborhood?.id,
        NeighborhoodName: neighborhood?.name,
      });

      setCurrentView("main");
      onClose?.();
      onNeighborhoodSelect(neighborhood);
    },
    [onClose, onNeighborhoodSelect],
  );

  // Handler for deleting a recent search
  const handleDeleteSearch = useCallback(
    (e: React.MouseEvent, search: RecentSearchResult) => {
      e.stopPropagation();

      // Track analytics
      AnalyticsService.trackGAEventHandler("allRecentSearchesSearchDeleted");
      AnalyticsService.trackMx("allRecentSearchesSearchDeleted", {
        SearchId: search?.id,
        SearchTerm: search?.search_term,
      });

      // Set the deleting ID for transition effect
      setDeletingSearchId(search.id);

      deleteSavedSearch({ id: search.id });
    },
    [deleteSavedSearch],
  );

  // Handle delete success/error
  useEffect(() => {
    if (deleteStatus === "success" && deletingSearchId !== null) {
      // Show success toast
      showToast(
        i18n.get("searchDeletedSuccessfully") || "Search deleted successfully",
        "success",
      );

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["allRecentSearches"] });
      queryClient.invalidateQueries({ queryKey: ["recentSearches"] });

      // Reset deleting ID after a short delay for transition
      setTimeout(() => {
        setDeletingSearchId(null);
      }, 300);
    } else if (deleteStatus === "error" && deletingSearchId !== null) {
      // Show error toast
      const errorMessage =
        (deleteError as Error)?.message ||
        i18n.get("failedToDeleteSearch") ||
        "Failed to delete search";
      showToast(errorMessage, "error");

      // Reset deleting ID
      setDeletingSearchId(null);
    }
  }, [
    deleteStatus,
    deletingSearchId,
    deleteError,
    showToast,
    queryClient,
    i18n,
  ]);

  // Handler for nearby places click with analytics
  const handleNearbyPlacesClick = useCallback(() => {
    // Track analytics
    AnalyticsService.trackGAEventHandler(
      "searchSuggestionsNearbyPlacesClicked",
    );
    AnalyticsService.trackMx("searchSuggestionsNearbyPlacesClicked", {});

    onNearbyPlacesClick();
  }, [onNearbyPlacesClick]);

  // Handler for inline recent search select with analytics
  const handleRecentSearchSelect = useCallback(
    (search: RecentSearchResult) => {
      // Track analytics
      AnalyticsService.trackGAEventHandler(
        "searchSuggestionsRecentSearchSelected",
      );
      AnalyticsService.trackMx("searchSuggestionsRecentSearchSelected", {
        SearchId: search?.id,
        SearchTerm: search?.search_term,
      });

      onRecentSearchSelect(search);
    },
    [onRecentSearchSelect],
  );

  // Handler for inline neighborhood select with analytics
  const handleNeighborhoodSelect = useCallback(
    (neighborhood: NeighborhoodSuggestion) => {
      // Track analytics
      AnalyticsService.trackGAEventHandler(
        "searchSuggestionsNeighborhoodSelected",
      );
      AnalyticsService.trackMx("searchSuggestionsNeighborhoodSelected", {
        NeighborhoodId: neighborhood?.id,
        NeighborhoodName: neighborhood?.name,
      });

      onNeighborhoodSelect(neighborhood);
    },
    [onNeighborhoodSelect],
  );

  if (!isOpen) return null;

  const isLoading =
    isRecentSearchesLoading || isNeighbourhoodSuggestionsLoading;

  // Render All Recent Searches View
  const renderAllRecentSearchesView = () => {
    let expandedItemIndex = 0; // Start at 0 for back button
    
    return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header with Back Button */}
      <div className="flex items-center gap-3 px-2 py-3 border-b border-border-token-brand-minimal flex-shrink-0">
        <button
          ref={(el) => {
            if (el) expandedItemRefs.current[0] = el;
          }}
          type="button"
          onClick={handleBack}
          onFocus={() => setExpandedHighlight(0)}
          className={cn(
            "p-1 rounded-lg transition-colors focus-blue",
            expandedHighlight === 0
              ? "bg-bg-brand-tertiary-active"
              : "hover:bg-bg-brand-secondary-hover"
          )}
          aria-label={i18n.get("back")}
        >
          <ArrowLeftIcon size={20} className="text-fg-neutral-strong" />
        </button>
        <span className="title-s-semibold text-fg-neutral-strong" aria-hidden="true">
          {i18n.get("allRecentSearches")}
        </span>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-auto px-2" role="region" aria-label={i18n.get("allRecentSearches")}>
        {isAllRecentSearchesLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-fg-brand-subtle" />
          </div>
        )}

        {!isAllRecentSearchesLoading && recentSearchSections.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ClockIcon size={48} className="text-fg-neutral-minimal mb-4" />
            <p className="label-m-medium text-fg-neutral-subtle">
              {i18n.get("noRecentSearches")}
            </p>
            <p className="body-s-regular text-fg-neutral-subtle mt-2">
              {i18n.get("yourSearchHistoryWillAppearHere")}
            </p>
          </div>
        )}

        {!isAllRecentSearchesLoading &&
          recentSearchSections.map((section) => (
            <div key={section.title} className="mb-4">
              {/* Section Header */}
              <div className="sticky top-0 py-2 z-10 bg-bg-brand-secondary-default">
                <p className="label-m-medium text-fg-neutral-subtle">
                  {section.title}
                </p>
                {section.subtitle && (
                  <p className="body-s-regular text-fg-neutral-subtle">
                    {section.subtitle}
                  </p>
                )}
              </div>

              {/* Section Items */}
              <div className="flex flex-col">
                {section.data.map((search, index) => {
                  expandedItemIndex++;
                  const currentExpandedIndex = expandedItemIndex;
                  const filtersText = formatFilters(search.filters);
                  return (
                  <div key={search.id}>
                    <div
                      className={cn(
                        "flex items-start justify-between gap-2 py-3 px-2 rounded-lg transition-colors",
                        expandedHighlight === currentExpandedIndex
                          ? "bg-bg-brand-tertiary-active"
                          : "hover:bg-bg-brand-secondary-hover",
                      )}
                    >
                      <button
                        ref={(el) => {
                          if (el) expandedItemRefs.current[currentExpandedIndex] = el;
                        }}
                        type="button"
                        onClick={() =>
                          handleRecentSearchSelectFromExpandedView(search)
                        }
                        onFocus={() => setExpandedHighlight(currentExpandedIndex)}
                        className="flex items-start gap-3 flex-1 min-w-0 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                        aria-label={`Recent search: ${search.search_term}${filtersText ? `, ${filtersText}` : ''}`}
                      >
                        <ClockIcon
                          size={20}
                          className="text-fg-neutral-subtle flex-shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <div className="flex flex-col items-start flex-1 min-w-0 gap-1">
                          <span className="label-m-medium text-fg-neutral-strong truncate w-full">
                            {search.search_term}
                          </span>
                          {filtersText && (
                            <span className="body-s-regular text-fg-neutral-subtle truncate w-full">
                              {truncateText(filtersText, 100)}
                            </span>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteSearch(e, search)}
                        disabled={isDeleting || deletingSearchId === search.id}
                        className={cn(
                          "p-1.5 rounded-full hover:bg-container-neutral-minimal transition-colors flex-shrink-0 self-start mt-0.5",
                          "focus:outline-none focus:ring-2 focus:ring-primary-500",
                          (isDeleting || deletingSearchId === search.id) &&
                            "opacity-50 cursor-not-allowed",
                        )}
                        aria-label={`Delete search: ${search.search_term}`}
                      >
                        {deletingSearchId === search.id ? (
                          <Loader2
                            size={16}
                            className="text-fg-neutral-subtle animate-spin"
                            aria-hidden="true"
                          />
                        ) : (
                          <XIcon size={16} className="text-fg-neutral-subtle" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    {index < section.data.length - 1 &&
                      deletingSearchId !== search.id && (
                        <div
                          className="h-px border border-border-token-neutral-minimal mx-2"
                          aria-hidden="true"
                        />
                      )}
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
  };

  // Render All Neighborhoods View
  const renderAllNeighborhoodsView = () => {
    let expandedItemIndex = 0; // Start at 0 for back button
    
    return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header with Back Button */}
      <div className="flex items-center gap-3 px-2 py-3 border-b border-border-token-brand-minimal flex-shrink-0">
        <button
          ref={(el) => {
            if (el) expandedItemRefs.current[0] = el;
          }}
          type="button"
          onClick={handleBack}
          onFocus={() => setExpandedHighlight(0)}
          className={cn(
            "p-1 rounded-lg transition-colors focus-blue",
            expandedHighlight === 0
              ? "bg-bg-brand-tertiary-active"
              : "hover:bg-bg-brand-secondary-hover"
          )}
          aria-label={i18n.get("back")}
        >
          <ArrowLeftIcon size={20} className="text-fg-neutral-strong" />
        </button>
        <span className="title-s-semibold text-fg-neutral-strong" aria-hidden="true">
          {i18n.get("allSuggestedBySociability")}
        </span>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-auto px-2" role="region" aria-label={i18n.get("allSuggestedBySociability")}>
        {isAllNeighbourhoodSuggestionsLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-fg-brand-subtle" />
          </div>
        )}

        {!isAllNeighbourhoodSuggestionsLoading &&
          allNeighborhoods.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MapPinIcon size={48} className="text-fg-neutral-minimal mb-4" />
              <p className="label-m-medium text-fg-neutral-subtle">
                {i18n.get("noNeighborhoods")}
              </p>
              <p className="body-s-regular text-fg-neutral-subtle mt-2">
                {i18n.get("noNeighbourhoodSuggestionsAvailable")}
              </p>
            </div>
          )}

        {!isAllNeighbourhoodSuggestionsLoading &&
          allNeighborhoods.length > 0 && (
            <div className="flex flex-col py-2">
              {allNeighborhoods.map((neighborhood, index) => {
                expandedItemIndex++;
                const currentExpandedIndex = expandedItemIndex;
                const filtersText = formatFilters(neighborhood.filters);
                return (
                  <div key={neighborhood.id}>
                    <button
                      ref={(el) => {
                        if (el) expandedItemRefs.current[currentExpandedIndex] = el;
                      }}
                      type="button"
                      onClick={() =>
                        handleNeighborhoodSelectFromExpandedView(neighborhood)
                      }
                      onFocus={() => setExpandedHighlight(currentExpandedIndex)}
                      className={cn(
                        "w-full flex items-start gap-3 py-3 px-2",
                        "rounded-lg transition-colors",
                        "focus:outline-none focus:ring-2 focus:ring-primary-500",
                        expandedHighlight === currentExpandedIndex
                          ? "bg-bg-brand-tertiary-active"
                          : "hover:bg-bg-brand-secondary-hover",
                      )}
                      aria-label={`Neighborhood: ${neighborhood.name}${neighborhood.description ? `, ${neighborhood.description}` : ''}`}
                    >
                      <MapPinIcon
                        size={20}
                        className="text-fg-neutral-subtle flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <div className="flex flex-col items-start flex-1 min-w-0 gap-1">
                        <span className="label-m-medium text-fg-neutral-strong truncate text-left w-full">
                          {neighborhood.name}
                        </span>
                        {neighborhood.description && (
                          <span className="body-s-regular text-fg-neutral-subtle truncate text-left w-full">
                            {neighborhood.description}
                          </span>
                        )}
                        {filtersText && (
                          <div className="flex items-baseline gap-2 w-full">
                            <span className="body-s-regular text-fg-neutral-subtle truncate">
                              {truncateText(filtersText, 100)}
                            </span>
                          </div>
                        )}
                      </div>
                    </button>
                    {index < allNeighborhoods.length - 1 && (
                      <div
                        className="h-px border border-border-token-neutral-minimal mx-2"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
  };

  // Helper to truncate text
  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Render Main View
  const renderMainView = () => {
    let itemIndex = 0;
    
    return (
    <>
      {/* Nearby Places Option */}
      <button
        ref={(el) => {
          if (el) itemRefs.current[itemIndex] = el;
        }}
        type="button"
        tabIndex={-1}
        onClick={handleNearbyPlacesClick}
        className={cn(
          "w-full flex items-center gap-4 px-2 py-3 min-h-[48px] text-left rounded-lg transition-colors focus-blue",
          highlightedIndex === itemIndex
            ? "bg-bg-brand-tertiary-active"
            : "hover:bg-bg-brand-secondary-hover"
        )}
        role="option"
        aria-selected={highlightedIndex === itemIndex}
        aria-label={`Search ${i18n.get("nearbyPlaces")}`}
      >
        <CrosshairIcon
          size={24}
          className="text-fg-neutral-subtle flex-shrink-0"
          aria-hidden="true"
        />
        <span className="label-m-medium text-fg-neutral-strong">
          {i18n.get("nearbyPlaces")}
        </span>
      </button>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-fg-brand-subtle" />
        </div>
      )}

      {/* Two Column Layout - Side by side on desktop, stacked on mobile/zoomed */}
      {!isLoading && (
        <div className={`flex flex-col ${!isZoomedIn ? 'md:flex-row' : ''} gap-3 mt-3`}>
          {/* Recent Searches Column */}
          {isLoggedIn &&
            displayedRecentSearches.length > 0 && (
              <div className="flex-1 flex flex-col gap-1 bg-bg-brand-secondary-default">
                <div className="flex items-center justify-between px-2 py-1">
                  <span className="body-l-semibold text-fg-neutral-subtle">
                    {i18n.get("recentSearches")}
                  </span>
                  {/* More link */}
                  {isLoggedIn && hasMoreRecentSearches && (() => {
                    itemIndex++;
                    const currentItemIndex = itemIndex;
                    return (
                      <button
                        ref={(el) => {
                          if (el) itemRefs.current[currentItemIndex] = el;
                        }}
                        type="button"
                        tabIndex={-1}
                        onClick={handleViewMoreRecentSearches}
                        className={cn(
                          "underline body-l-semibold text-fg-neutral-strong min-h-[48px] min-w-[44px] px-2 py-2 -my-2 -mr-2 rounded-lg transition-colors focus-blue touch-manipulation",
                          highlightedIndex === currentItemIndex
                            ? "bg-bg-brand-tertiary-active"
                            : "hover:bg-bg-brand-secondary-hover"
                        )}
                        role="option"
                        aria-selected={highlightedIndex === currentItemIndex}
                        aria-label={`${i18n.get("more")} ${i18n.get("recentSearches")}`}
                      >
                        {i18n.get("more")}
                      </button>
                    );
                  })()}
                </div>
                <div className="flex flex-col">
                  {isLoggedIn &&
                    displayedRecentSearches.map((search, index) => {
                      itemIndex++; // Increment after nearby places
                      const currentItemIndex = itemIndex;
                      const filtersText = formatFilters(search.filters);
                      return (
                        <React.Fragment key={search.id}>
                          <button
                            ref={(el) => {
                              if (el) itemRefs.current[currentItemIndex] = el;
                            }}
                            type="button"
                            tabIndex={-1}
                            onClick={() => handleRecentSearchSelect(search)}
                            className={cn(
                              "w-full flex items-center gap-4 p-2 text-left rounded-lg transition-colors focus-blue",
                              filtersText ? "min-h-[58px]" : "min-h-[48px]",
                              highlightedIndex === currentItemIndex
                                ? "bg-bg-brand-tertiary-active"
                                : "hover:bg-bg-brand-secondary-hover"
                            )}
                            role="option"
                            aria-selected={highlightedIndex === currentItemIndex}
                            aria-label={`Search ${search.search_term}`}
                          >
                            <ClockIcon
                              size={24}
                              className="text-fg-neutral-subtle flex-shrink-0"
                              aria-hidden="true"
                            />

                            <div className="flex flex-col gap-1 flex-1 min-w-0">
                              <span className="label-m-medium text-fg-neutral-strong truncate">
                                {search.search_term.charAt(0).toUpperCase() +
                                  search.search_term.slice(1)}
                              </span>
                              {filtersText && (
                                <div className="flex items-baseline gap-2">
                                  <span className="body-s-regular text-fg-neutral-subtle truncate">
                                    {truncateText(filtersText, 50)}
                                  </span>
                                </div>
                              )}
                            </div>


                           
                          </button>
                          {index < displayedRecentSearches.length - 1 && (
                            <div
                              className="h-px border border-border-token-neutral-minimal mx-2"
                              aria-hidden="true"
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  {isLoggedIn && recentSearches.length === 0 && (
                    <p className="px-2 py-4 label-m-medium text-fg-neutral-subtle">
                      {i18n.get("noRecentSearches")}
                    </p>
                  )}                
                </div>
              </div>
          )}


         

          {/* Neighborhoods Column */}
          <div className={cn(
            "flex-1 flex flex-col gap-1 bg-bg-brand-secondary-default",
            !isNeighbourhoodSuggestionsLoading && neighborhoods.length === 0 ? "md:hidden" : ""
          )}>
             {/* Login to view link */}
          {!isLoggedIn && (
            
            <div className="flex items-center justify-between px-2 py-1">
              <span className="body-l-semibold text-fg-neutral-subtle">
                {i18n.get("recentSearches")}
              </span>
              <button
                type="button"
                tabIndex={-1}
                onClick={handleLoginClick}
                className={cn(
                  "underline body-l-semibold text-fg-neutral-strong min-h-[48px] min-w-[44px] px-2 py-2 -my-2 -mr-2 rounded-lg transition-colors focus-blue touch-manipulation hover:bg-bg-brand-secondary-hover",                  
                )}
              >
                {i18n.get("loginToView")}
              </button>
            </div>
        )}

            <div className="flex items-center justify-between px-2 py-1">
              <span className="body-l-semibold text-fg-neutral-subtle">
                {i18n.get("suggestedBySociability")}
              </span>
              {hasMoreNeighborhoods && (() => {
                itemIndex++;
                const currentItemIndex = itemIndex;
                return (
                <button
                  ref={(el) => {
                    if (el) itemRefs.current[currentItemIndex] = el;
                  }}
                  type="button"
                  tabIndex={-1}
                  onClick={handleViewMoreNeighborhoods}
                  className={cn(
                    "underline body-l-semibold text-fg-neutral-strong min-h-[48px] min-w-[44px] px-2 py-2 -my-2 -mr-2 rounded-lg transition-colors focus-blue touch-manipulation",
                    highlightedIndex === currentItemIndex
                      ? "bg-bg-brand-tertiary-active"
                      : "hover:bg-bg-brand-secondary-hover"
                  )}
                  role="option"
                  aria-selected={highlightedIndex === currentItemIndex}
                  aria-label={`${i18n.get("more")} ${i18n.get("suggestedBySociability")}`}
                >
                  {i18n.get("more")}
                </button>
                );
              })()}
            </div>
            <div className="flex flex-col">
              {displayedNeighborhoods.map((neighborhood, index) => {
                itemIndex++; // Increment for each neighborhood
                const currentItemIndex = itemIndex;
                const filtersText = formatFilters(neighborhood.filters);
                return (
                  <React.Fragment key={neighborhood.id}>
                    <button
                      ref={(el) => {
                        if (el) itemRefs.current[currentItemIndex] = el;
                      }}
                      type="button"
                      tabIndex={-1}
                      onClick={() => handleNeighborhoodSelect(neighborhood)}
                      className={cn(
                        "w-full flex items-start gap-4 p-2 text-left rounded-lg transition-colors focus-blue",
                        filtersText ? "min-h-[58px]" : "min-h-[48px]",
                        highlightedIndex === currentItemIndex
                          ? "bg-bg-brand-tertiary-active"
                          : "hover:bg-bg-brand-secondary-hover"
                      )}
                      role="option"
                      aria-selected={highlightedIndex === currentItemIndex}
                      aria-label={`Search ${neighborhood.name}${filtersText ? `, ${filtersText}` : ''}`}
                    >
                      <MapTrifoldIcon
                        size={24}
                        className="text-fg-neutral-subtle flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <span className="label-m-medium text-fg-neutral-strong truncate">
                          {neighborhood.name}
                        </span>
                        {filtersText && (
                          <div className="flex items-baseline gap-2">                           
                            <span className="body-s-regular text-fg-neutral-subtle truncate">
                              {truncateText(filtersText, 50)}
                            </span>
                          </div>
                        )}
                      </div>
                    </button>
                    {index < displayedNeighborhoods.length - 1 && (
                      <div
                        className="h-px border border-border-token-neutral-minimal mx-2"
                        aria-hidden="true"
                      />
                    )}
                  </React.Fragment>
                );
              })}
              {neighborhoods.length === 0 && (
                <p className="px-2 py-4 label-m-medium text-fg-neutral-subtle">
                  {i18n.get("noNeighborhoods")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
  };

  return (
    <div
      ref={dropdownRef}
      id="search-suggestions-listbox"
      className={cn(
        "bg-bg-brand-secondary-default rounded-xl shadow-lg border border-neutral-100 p-3",
        "w-full md:w-[600px] lg:w-[750px] max-h-[60vh]",
        currentView === "main"
          ? "overflow-y-auto"
          : "overflow-hidden flex flex-col",
        className,
      )}
      role="listbox"
      aria-label={i18n.get("searchSuggestions")}
    >
      {currentView === "main" && renderMainView()}
      {currentView === "allRecentSearches" && renderAllRecentSearchesView()}
      {currentView === "allNeighborhoods" && renderAllNeighborhoodsView()}
    </div>
  );
}

export default SearchSuggestionsDropdown;
